// Post-take "reset gain" reminder.
//
// Only fires when adjacent cues differ in dynamic profile by >= 2 categories
// (e.g. shout → normal, or whisper → loud). The reminder explains WHAT the
// actor changed for the previous take and WHY they need to undo it now.
//
// Tone: friendly heads-up, never red. One reminder at a time.
/** dB step recommended per category transition. Indexed by category. */
const CATEGORY_DB = {
    whisper: +6,
    normal: 0,
    loud: -3,
    shout: -6,
    extreme: -3,
};
/**
 * Build a reset-gain banner shown between two adjacent takes whose dynamic
 * categories differ enough to warrant re-checking the gain knob.
 *
 * @param previous category of the take just finished
 * @param next     category of the cue about to be recorded
 * @returns null when no reset is needed (delta < 4 dB)
 */
export function resetReminder(previous, next) {
    const prevDb = CATEGORY_DB[previous];
    const nextDb = CATEGORY_DB[next];
    const delta = nextDb - prevDb; // positive ⇒ turn UP, negative ⇒ turn DOWN
    if (Math.abs(delta) < 4)
        return null;
    const direction = delta > 0 ? 'up' : 'down';
    const magnitude = Math.abs(delta);
    const sign = delta > 0 ? '+' : '-';
    const prevLabelZh = LABEL[previous].zh;
    const nextLabelZh = LABEL[next].zh;
    const prevLabelEn = LABEL[previous].en;
    const nextLabelEn = LABEL[next].en;
    const prevLabelEs = LABEL[previous].es;
    const nextLabelEs = LABEL[next].es;
    return {
        icon: '🎚️',
        zh: direction === 'up'
            ? `刚才录${prevLabelZh}拧小了 ${Math.abs(prevDb)} dB。下一条是${nextLabelZh},记得 ${sign}${magnitude} dB 拨回来。`
            : `刚才录${prevLabelZh}拨大了 ${Math.abs(prevDb)} dB。下一条是${nextLabelZh},记得 ${sign}${magnitude} dB 收回来。`,
        en: direction === 'up'
            ? `You turned gain down ${Math.abs(prevDb)} dB for that ${prevLabelEn}. Next line is ${nextLabelEn} — remember to bring it back ${sign}${magnitude} dB.`
            : `You turned gain up ${Math.abs(prevDb)} dB for that ${prevLabelEn}. Next line is ${nextLabelEn} — trim ${sign}${magnitude} dB back down.`,
        es: direction === 'up'
            ? `Bajó la ganancia ${Math.abs(prevDb)} dB para ese ${prevLabelEs}. La siguiente toma es ${nextLabelEs} — recuerde subirla de nuevo ${sign}${magnitude} dB.`
            : `Subió la ganancia ${Math.abs(prevDb)} dB para ese ${prevLabelEs}. La siguiente toma es ${nextLabelEs} — bájela de nuevo ${sign}${magnitude} dB.`,
    };
}
/** Human-readable category labels used inside the reset reminder body. */
const LABEL = {
    whisper: { zh: '耳语', en: 'whisper', es: 'susurro' },
    normal: { zh: '平静对话', en: 'normal dialogue', es: 'diálogo normal' },
    loud: { zh: '响度提高', en: 'raised voice', es: 'voz elevada' },
    shout: { zh: '喊叫', en: 'shout', es: 'grito' },
    extreme: { zh: '极端动态', en: 'extreme-dynamic line', es: 'línea de dinámica extrema' },
};
